<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRetentionAccess } from 'dashboard/composables/useRetentionAccess';
import RetentionAPI from 'dashboard/api/retention';
import { useStore } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import RetentionMessage from 'dashboard/components-next/message/RetentionMessage.vue';
import AttachmentsPreview from 'dashboard/components/widgets/AttachmentsPreview.vue';
import { resolveMaximumFileUploadSize } from 'shared/helpers/FileHelper';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();
const { member, loaded, unreadConversationIds, unreadCount, revoke } =
  useRetentionAccess();
const unreadConversations = computed(
  () => new Set(unreadConversationIds.value)
);
const tabs = computed(() => [
  {
    id: 'active',
    label: unreadCount.value
      ? `${t('RETENTION.TABS.active')} (${unreadCount.value})`
      : t('RETENTION.TABS.active'),
    empty: t('RETENTION.EMPTY.active'),
  },
  {
    id: 'search',
    label: t('RETENTION.TABS.search'),
    empty: t('RETENTION.EMPTY.search'),
  },
  {
    id: 'history',
    label: t('RETENTION.TABS.history'),
    empty: t('RETENTION.EMPTY.history'),
  },
]);
const { accountId } = useAccount();
const tab = ref('active');
const query = ref('');
const rows = ref([]);
const selected = ref(null);
const savedSession = ref(null);
const openedRows = ref({});
const tabQueries = ref({});
const messages = ref([]);
const drafts = ref({});
const fileDrafts = ref({});
const fileInput = ref(null);
const error = ref('');
const pendingSnapshot = ref(null);
const busy = ref(false);
const loading = ref(false);
const loadingChat = ref(false);
const hasMore = ref(false);
const hasOlder = ref(false);
const editingMessageId = ref(null);
const editingExpectedContent = ref(null);
const confirmingDeleteId = ref(null);
const deletingExpectedContent = ref(null);
const page = ref(1);
const transcript = ref(null);
let listSequence = 0;
let chatSequence = 0;
const pendingSends = new Map();
let timer;
let listTimer;
let searchTimer;

const draft = computed({
  get: () => drafts.value[selected.value?.id] || '',
  set: value => {
    drafts.value[selected.value?.id] = value;
  },
});
const files = computed(() => fileDrafts.value[selected.value?.id] || []);
const canSend = computed(() => !!draft.value.trim() || !!files.value.length);
const title = computed(
  () => selected.value?.contact?.name || savedSession.value?.contact?.name
);
const subtitle = computed(
  () => selected.value?.inbox?.name || savedSession.value?.inbox?.name
);
const formatDate = value => new Date(value).toLocaleString();

function clearChat(forget = true) {
  if (forget) delete openedRows.value[tab.value];
  chatSequence += 1;
  selected.value = null;
  savedSession.value = null;
  messages.value = [];
  hasOlder.value = false;
  editingMessageId.value = null;
  editingExpectedContent.value = null;
  confirmingDeleteId.value = null;
  deletingExpectedContent.value = null;
  loadingChat.value = false;
}

function handleError(exception) {
  error.value =
    exception.response?.data?.error ||
    exception.response?.data?.message ||
    t('RETENTION.ERROR');
  if ([401, 403].includes(exception.response?.status)) {
    revoke();
  }
}

async function markRead() {
  if (
    document.hidden ||
    !selected.value?.session_id ||
    !unreadConversations.value.has(selected.value.id)
  )
    return;
  const lastMessage = messages.value.at(-1);
  if (lastMessage?.retention_session_id !== selected.value.session_id) return;
  await store.dispatch('notifications/readRetention', {
    conversationId: selected.value.id,
    sessionId: selected.value.session_id,
    lastMessageId: lastMessage.id,
  });
}

async function scrollToBottom() {
  await nextTick();
  if (transcript.value)
    transcript.value.scrollTop = transcript.value.scrollHeight;
}

async function openRow(row) {
  if (!member.value) return;
  clearChat(false);
  openedRows.value[tab.value] = row;
  error.value = '';
  chatSequence += 1;
  const sequence = chatSequence;
  loadingChat.value = true;
  try {
    if (tab.value === 'history') {
      const { data } = await RetentionAPI.snapshot(row.id);
      if (sequence !== chatSequence) return;
      savedSession.value = data.session;
      messages.value = data.snapshot.messages;
    } else {
      const { data } = await RetentionAPI.conversation(row.id);
      if (sequence !== chatSequence) return;
      selected.value = data.conversation;
      messages.value = data.messages;
      hasOlder.value = data.has_more;
    }
    await scrollToBottom();
    if (tab.value !== 'history') await markRead();
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  } finally {
    if (sequence === chatSequence) loadingChat.value = false;
  }
}

async function loadList(more = false) {
  clearTimeout(searchTimer);
  if (!member.value) return;
  listSequence += 1;
  const sequence = listSequence;
  const requestedPage = more ? page.value + 1 : 1;
  loading.value = true;
  error.value = '';
  try {
    const params = {
      page: requestedPage,
      q: tab.value === 'active' ? undefined : query.value.trim(),
    };
    if (tab.value === 'search' && !params.q) {
      rows.value = [];
      hasMore.value = false;
      return;
    }
    const { data } =
      tab.value === 'history'
        ? await RetentionAPI.history(params)
        : await RetentionAPI.conversations(params);
    if (sequence !== listSequence) return;
    const results = data.sessions || data.conversations;
    rows.value = more ? [...rows.value, ...results] : results;
    hasMore.value = data.has_more;
    page.value = requestedPage;
    if (
      tab.value === 'history' &&
      pendingSnapshot.value &&
      results.some(row => row.id === pendingSnapshot.value.id)
    ) {
      const saved = pendingSnapshot.value;
      pendingSnapshot.value = null;
      await openRow(saved);
    }
  } catch (exception) {
    if (sequence === listSequence) handleError(exception);
  } finally {
    if (sequence === listSequence) loading.value = false;
  }
}

function canManageMessage(message) {
  return (
    !!message &&
    !!selected.value?.session_id &&
    selected.value.session_id === message.retention_session_id &&
    [1, 'outgoing'].includes(message.message_type) &&
    message.sender_type === 'User' &&
    (message.source_id || message.status === 'failed') &&
    !message.content_attributes?.deleted
  );
}

function currentMessage(messageId) {
  return messages.value.find(message => message.id === messageId);
}

function cancelEdit() {
  editingMessageId.value = null;
  editingExpectedContent.value = null;
}

function cancelDelete() {
  confirmingDeleteId.value = null;
  deletingExpectedContent.value = null;
}

async function refreshChat(older = false) {
  if (!selected.value || busy.value || loadingChat.value) return;
  const sequence = chatSequence;
  const id = selected.value.id;
  const shouldScroll =
    transcript.value &&
    transcript.value.scrollHeight -
      transcript.value.scrollTop -
      transcript.value.clientHeight <
      80;
  try {
    const { data } = await RetentionAPI.conversation(
      id,
      older ? { before: messages.value[0]?.id } : {}
    );
    if (sequence !== chatSequence) return;
    if (selected.value?.session_id && !data.conversation.session_id) {
      clearChat();
      loadList();
      return;
    }
    selected.value = data.conversation;
    const merged = new Map(
      messages.value.map(message => [message.id, message])
    );
    data.messages.forEach(message => merged.set(message.id, message));
    messages.value = [...merged.values()].sort((a, b) => a.id - b.id);
    if (
      editingMessageId.value &&
      !canManageMessage(currentMessage(editingMessageId.value))
    )
      cancelEdit();
    if (
      confirmingDeleteId.value &&
      !canManageMessage(currentMessage(confirmingDeleteId.value))
    )
      cancelDelete();
    if (older) hasOlder.value = data.has_more;
    if (!older && shouldScroll) await scrollToBottom();
    if (!older && shouldScroll) await markRead();
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  }
}

function replaceFiles(nextFiles, id = selected.value?.id) {
  const previous = fileDrafts.value[id] || [];
  previous.forEach(file => {
    if (!nextFiles.includes(file)) URL.revokeObjectURL(file.thumb);
  });
  fileDrafts.value[id] = nextFiles;
}

function clearFiles() {
  pendingSends.clear();
  Object.keys(fileDrafts.value).forEach(id => replaceFiles([], id));
  fileDrafts.value = {};
}

function addFiles(incoming) {
  if (busy.value || !selected.value || !member.value) return;
  const uploads = Array.from(incoming);
  if (uploads.length + files.value.length > 15) {
    error.value = t('RETENTION.UPLOAD_COUNT');
    return;
  }
  const limit = resolveMaximumFileUploadSize(
    store.getters?.['globalConfig/get']?.maximumFileUploadSize
  );
  if (uploads.some(file => file.size > limit * 1024 * 1024)) {
    error.value = t('RETENTION.UPLOAD_SIZE', { limit });
    return;
  }
  error.value = '';
  replaceFiles([
    ...files.value,
    ...uploads.map(resource => ({
      id: crypto.randomUUID(),
      resource,
      thumb: URL.createObjectURL(resource),
    })),
  ]);
}

function selectFiles(event) {
  addFiles(event.target.files);
  event.target.value = '';
}

function pasteFiles(event) {
  if (!event.clipboardData?.files.length) return;
  event.preventDefault();
  addFiles(event.clipboardData.files);
}

async function send() {
  if (busy.value || !canSend.value || !selected.value) return;
  const id = selected.value.id;
  const content = draft.value;
  const attachments = files.value.map(file => file.resource);
  let pendingSend = pendingSends.get(id);
  const sequence = chatSequence;
  if (
    !pendingSend ||
    pendingSend.content !== content ||
    pendingSend.id !== id ||
    pendingSend.attachments.length !== attachments.length ||
    pendingSend.attachments.some((file, index) => file !== attachments[index])
  ) {
    pendingSend = {
      id,
      content,
      request_id: crypto.randomUUID(),
      session_id: selected.value.session_id,
      attachments,
    };
    pendingSends.set(id, pendingSend);
  }
  busy.value = true;
  error.value = '';
  try {
    const { data } = await RetentionAPI.send(id, pendingSend);
    if (sequence !== chatSequence) return;
    selected.value = data.conversation;
    const merged = new Map(
      messages.value.map(message => [message.id, message])
    );
    data.messages.forEach(message => merged.set(message.id, message));
    messages.value = [...merged.values()].sort((a, b) => a.id - b.id);
    drafts.value[id] = '';
    replaceFiles([], id);
    pendingSends.delete(id);
    await scrollToBottom();
    await loadList();
  } catch (exception) {
    if (sequence === chatSequence) {
      handleError(exception);
      if (exception.response?.status === 409) pendingSends.delete(id);
    }
  } finally {
    busy.value = false;
  }
}

async function complete() {
  if (busy.value || !selected.value?.session_id || canSend.value) return;
  busy.value = true;
  error.value = '';
  const sequence = chatSequence;
  try {
    const { data } = await RetentionAPI.complete(
      selected.value.id,
      selected.value.session_id
    );
    if (sequence !== chatSequence) return;
    delete openedRows.value[tab.value];
    query.value = '';
    tab.value = 'history';
    await nextTick();
    if (data.snapshot_pending) {
      pendingSnapshot.value = data.session;
      clearChat();
      return;
    }
    await openRow(data.session);
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  } finally {
    busy.value = false;
  }
}

async function retry(messageId) {
  if (busy.value || !selected.value?.session_id) return;
  busy.value = true;
  const sequence = chatSequence;
  try {
    await RetentionAPI.retry(selected.value.id, messageId);
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  } finally {
    busy.value = false;
    refreshChat();
  }
}

function replaceMessage(message) {
  messages.value = messages.value.map(existing =>
    existing.id === message.id ? message : existing
  );
}

function startEdit(messageId) {
  const message = currentMessage(messageId);
  if (!canManageMessage(message)) return;
  cancelDelete();
  editingMessageId.value = messageId;
  editingExpectedContent.value = message.content;
}

function startDelete(messageId) {
  const message = currentMessage(messageId);
  if (!canManageMessage(message)) return;
  cancelEdit();
  confirmingDeleteId.value = messageId;
  deletingExpectedContent.value = message.content;
}

async function editMessage(messageId, content) {
  const message = currentMessage(messageId);
  if (
    busy.value ||
    editingMessageId.value !== messageId ||
    !canManageMessage(message)
  )
    return;
  busy.value = true;
  error.value = '';
  const sequence = chatSequence;
  try {
    const { data } = await RetentionAPI.editMessage(
      selected.value.id,
      messageId,
      {
        sessionId: selected.value.session_id,
        content,
        expectedContent: editingExpectedContent.value,
      }
    );
    if (sequence !== chatSequence) return;
    replaceMessage(data.message);
    cancelEdit();
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  } finally {
    busy.value = false;
  }
}

async function deleteMessage(messageId) {
  const message = currentMessage(messageId);
  if (
    busy.value ||
    confirmingDeleteId.value !== messageId ||
    !canManageMessage(message)
  )
    return;
  busy.value = true;
  error.value = '';
  const sequence = chatSequence;
  try {
    const { data } = await RetentionAPI.deleteMessage(
      selected.value.id,
      messageId,
      {
        sessionId: selected.value.session_id,
        expectedContent: deletingExpectedContent.value,
      }
    );
    if (sequence !== chatSequence) return;
    replaceMessage(data.message);
    cancelDelete();
  } catch (exception) {
    if (sequence === chatSequence) handleError(exception);
  } finally {
    busy.value = false;
  }
}

function onEnter(event) {
  if (event.isComposing) return;
  event.preventDefault();
  send();
}

async function openNotificationTarget() {
  if (!member.value) return;
  if (route.query.snapshot) {
    tab.value = 'history';
    await openRow({ id: route.query.snapshot });
  } else if (route.query.conversation) {
    tab.value = 'active';
    await openRow({ id: Number(route.query.conversation) });
  }
}

watch([member, () => route.query], openNotificationTarget, { flush: 'post' });

function refreshWorkspace(event) {
  if (event?.account_id && Number(event.account_id) !== accountId.value) return;
  if (!member.value || document.hidden) return;
  if (!busy.value && !loading.value) loadList();
  refreshChat();
}

function refreshOnFocus() {
  refreshWorkspace();
}

watch(
  [tab, member, accountId],
  (
    [currentTab, currentMember, currentAccount],
    [previousTab, , previousAccount]
  ) => {
    clearTimeout(searchTimer);
    listSequence += 1;
    clearChat(false);
    if (!currentMember || currentAccount !== previousAccount) {
      openedRows.value = {};
      tabQueries.value = {};
      query.value = '';
    } else if (currentTab !== previousTab) {
      tabQueries.value[previousTab] = query.value;
      query.value = tabQueries.value[currentTab] || '';
    }
    rows.value = [];
    loading.value = false;
    if (!member.value) {
      drafts.value = {};
      clearFiles();
      pendingSnapshot.value = null;
    }
    hasMore.value = false;
    loadList();
    if (openedRows.value[currentTab]) openRow(openedRows.value[currentTab]);
  },
  { flush: 'sync' }
);
watch(
  query,
  () => {
    clearTimeout(searchTimer);
    if (tab.value === 'active' || !member.value) return;
    listSequence += 1;
    searchTimer = setTimeout(() => loadList(), 300);
  },
  { flush: 'sync' }
);
watch(
  [member, loaded],
  () => {
    if (loaded.value && !member.value) {
      router.replace({ name: 'home', params: { accountId: accountId.value } });
    }
  },
  { immediate: true, flush: 'sync' }
);
watch(accountId, () => {
  drafts.value = {};
  clearFiles();
});
onMounted(() => {
  emitter.on('retention.changed', refreshWorkspace);
  emitter.on(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshOnFocus);
  window.addEventListener('focus', refreshOnFocus);
  if (member.value) {
    loadList();
    openNotificationTarget();
  }
  timer = setInterval(() => {
    if (member.value && !document.hidden) {
      refreshChat();
      if (pendingSnapshot.value) refreshWorkspace();
    }
  }, 4000);
  listTimer = setInterval(refreshOnFocus, 15000);
});
onUnmounted(() => {
  clearFiles();
  emitter.off('retention.changed', refreshWorkspace);
  emitter.off(BUS_EVENTS.WEBSOCKET_RECONNECT, refreshOnFocus);
  window.removeEventListener('focus', refreshOnFocus);
  listSequence += 1;
  chatSequence += 1;
  clearInterval(timer);
  clearInterval(listTimer);
  clearTimeout(searchTimer);
});
</script>

<template>
  <section
    class="flex flex-col w-full h-full min-w-0 bg-n-background text-n-slate-12"
  >
    <header class="px-6 py-4 border-b border-n-weak">
      <h1 class="text-xl font-semibold">{{ t('RETENTION.TITLE') }}</h1>
    </header>
    <p v-if="!loaded" role="status" class="p-6 text-n-slate-11">
      {{ t('RETENTION.LOADING') }}
    </p>
    <p v-else-if="!member" role="status" class="p-6 text-n-slate-11">
      {{ t('RETENTION.NO_ACCESS') }}
    </p>
    <template v-else>
      <p
        v-if="pendingSnapshot"
        role="status"
        class="px-6 py-3 text-sm bg-n-blue-3 text-n-blue-text"
      >
        {{ t('RETENTION.SNAPSHOT_PENDING') }}
      </p>
      <div
        v-if="error"
        role="alert"
        class="px-6 py-3 text-sm bg-n-ruby-3 text-n-ruby-11"
      >
        {{ error }}
      </div>
      <div class="flex flex-1 min-h-0 overflow-hidden">
        <aside
          class="flex flex-col shrink-0 w-full md:w-80 border-r border-n-weak"
          :class="{ 'hidden md:flex': selected || savedSession || loadingChat }"
        >
          <nav
            :aria-label="t('RETENTION.TITLE')"
            class="flex gap-1 p-3 border-b border-n-weak"
          >
            <Button
              v-for="view in tabs"
              :key="view.id"
              :label="view.label"
              :variant="tab === view.id ? 'solid' : 'ghost'"
              size="sm"
              :disabled="busy"
              @click="tab = view.id"
            />
          </nav>
          <form
            v-if="tab !== 'active'"
            class="p-3 flex gap-2"
            @submit.prevent="loadList()"
          >
            <input
              v-model="query"
              type="search"
              :aria-label="t('RETENTION.SEARCH')"
              :placeholder="t('RETENTION.SEARCH_PLACEHOLDER')"
              class="!mb-0 min-w-0 flex-1 !text-n-slate-12"
            />
            <Button
              type="submit"
              icon="i-lucide-search"
              :aria-label="t('RETENTION.FIND')"
              :disabled="loading"
            />
          </form>
          <div class="overflow-y-auto flex-1" :aria-busy="loading">
            <p
              v-if="loading && !rows.length"
              role="status"
              class="p-4 text-sm text-n-slate-11"
            >
              {{ t('RETENTION.LOADING') }}
            </p>
            <p v-else-if="!rows.length" class="p-4 text-sm text-n-slate-11">
              {{ tabs.find(view => view.id === tab).empty }}
            </p>
            <button
              v-for="row in rows"
              :key="row.id"
              class="w-full px-4 py-3 text-left text-n-slate-12 border-b border-n-weak hover:bg-n-alpha-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-n-brand"
              :class="{
                'bg-n-alpha-2':
                  row.id ===
                  (tab === 'history' ? savedSession?.id : selected?.id),
              }"
              :disabled="busy"
              @click="openRow(row)"
            >
              <span class="flex items-center gap-2">
                <span class="font-medium truncate flex-1">{{
                  row.contact.name
                }}</span>
                <span
                  v-if="tab !== 'history' && unreadConversations.has(row.id)"
                  class="shrink-0 rounded-md bg-n-blue-3 px-1.5 py-0.5 text-xs font-medium text-n-blue-text"
                >
                  {{ t('RETENTION.UNREAD') }}
                </span>
              </span>
              <span class="block text-xs text-n-slate-11 truncate">
                {{
                  t('RETENTION.ROW_META', {
                    inbox: row.inbox.name,
                    id: row.conversation_id || row.id,
                  })
                }}
              </span>
              <span
                v-if="row.completed_at"
                class="block mt-1 text-xs text-n-slate-10"
              >
                {{ formatDate(row.completed_at) }}
              </span>
              <span
                v-else-if="row.session_id"
                class="block mt-1 text-xs text-n-blue-11"
              >
                {{ t('RETENTION.ACTIVE') }}
              </span>
            </button>
            <Button
              v-if="hasMore"
              :label="t('RETENTION.MORE')"
              variant="ghost"
              class="m-3"
              :disabled="loading"
              @click="loadList(true)"
            />
          </div>
        </aside>
        <main
          class="flex-1 flex flex-col min-w-0 min-h-0"
          :class="{
            'hidden md:flex': !selected && !savedSession && !loadingChat,
          }"
        >
          <template v-if="selected || savedSession">
            <div
              class="flex items-center justify-between gap-3 px-5 py-3 border-b border-n-weak"
            >
              <div class="flex gap-2 min-w-0 items-center">
                <Button
                  class="md:hidden"
                  icon="i-lucide-arrow-left"
                  variant="ghost"
                  :aria-label="t('RETENTION.BACK')"
                  :disabled="busy"
                  @click="clearChat"
                />
                <div class="min-w-0">
                  <h2 class="font-semibold truncate">{{ title }}</h2>
                  <p class="text-xs text-n-slate-11">{{ subtitle }}</p>
                </div>
              </div>
              <Button
                v-if="selected?.session_id"
                :label="t('RETENTION.COMPLETE')"
                icon="i-lucide-check"
                :disabled="
                  busy ||
                  canSend ||
                  editingMessageId !== null ||
                  confirmingDeleteId !== null
                "
                @click="complete"
              />
            </div>
            <p
              class="px-5 py-2 text-xs text-n-slate-11 bg-n-alpha-1"
              role="status"
            >
              {{
                savedSession
                  ? t('RETENTION.SNAPSHOT', {
                      date: formatDate(savedSession.completed_at),
                    })
                  : selected.session_id
                    ? t('RETENTION.ACTIVE_NOTE')
                    : t('RETENTION.PREVIEW_NOTE')
              }}
            </p>
            <div
              ref="transcript"
              class="flex-1 overflow-y-auto px-5 py-4 space-y-5"
            >
              <Button
                v-if="hasOlder"
                :label="t('RETENTION.OLDER')"
                variant="ghost"
                @click="refreshChat(true)"
              />
              <RetentionMessage
                v-for="message in messages"
                :key="message.id"
                :message="message"
                :can-retry="
                  !busy && selected?.session_id === message.retention_session_id
                "
                :can-manage="canManageMessage(message)"
                :editing="editingMessageId === message.id"
                :confirming-delete="confirmingDeleteId === message.id"
                :busy="busy"
                @retry="retry"
                @start-edit="startEdit"
                @cancel-edit="cancelEdit"
                @save-edit="editMessage"
                @start-delete="startDelete"
                @cancel-delete="cancelDelete"
                @confirm-delete="deleteMessage"
              />
            </div>
            <form
              v-if="selected"
              class="p-4 border-t border-n-weak"
              @submit.prevent="send"
              @dragover.prevent
              @drop.prevent="addFiles($event.dataTransfer.files)"
            >
              <fieldset v-if="files.length" :disabled="busy" class="mb-2">
                <AttachmentsPreview
                  :attachments="files"
                  @remove-attachment="replaceFiles"
                />
              </fieldset>
              <input
                ref="fileInput"
                type="file"
                multiple
                class="hidden"
                :aria-label="t('RETENTION.ADD_FILES')"
                :disabled="busy"
                @change="selectFiles"
              />
              <label for="retention-reply" class="sr-only">{{
                t('RETENTION.REPLY')
              }}</label>
              <textarea
                id="retention-reply"
                v-model="draft"
                :placeholder="t('RETENTION.REPLY')"
                rows="3"
                class="!mb-2 resize-none !text-n-slate-12"
                :disabled="busy"
                @keydown.enter.exact="onEnter"
                @paste="pasteFiles"
              />
              <div
                class="flex items-center justify-end sm:justify-between gap-3"
              >
                <Button
                  type="button"
                  icon="i-lucide-paperclip"
                  variant="ghost"
                  :aria-label="t('RETENTION.ADD_FILES')"
                  :disabled="busy"
                  @click="fileInput.click()"
                />
                <span class="hidden sm:inline text-xs text-n-slate-10">{{
                  t('RETENTION.SEND_HINT')
                }}</span>
                <Button
                  type="submit"
                  :label="
                    selected.session_id
                      ? t('RETENTION.SEND')
                      : t('RETENTION.START_SEND')
                  "
                  icon="i-lucide-send"
                  :disabled="busy || !canSend"
                  :is-loading="busy"
                />
              </div>
            </form>
          </template>
          <div
            v-else
            class="flex-1 flex items-center justify-center p-8 text-n-slate-11 text-sm"
          >
            {{ loadingChat ? t('RETENTION.LOADING') : t('RETENTION.SELECT') }}
          </div>
        </main>
      </div>
    </template>
  </section>
</template>
